<script setup lang="ts">
import type { OpenFormPropertyPayload, PropertyContactSectionProps } from '.';

import { deepMergeLocales } from '@/utils/deepMerge';
import { createLocaleTranslator } from '@/utils/localeMessages';
import { useContactForm } from '~/composables/useContactForm';
import { EventBusKeys } from '~/core/helpers/symbols';
import { getAgentFirstName, getAgentName } from '~~/utils';

import { ContactFormModal } from '~/components/ContactFormModal';
import contactFormLocale from '~/components/ContactFormModal/ContactFormModal.locale.json';
import { ContactFormSection } from '~/components/ContactFormSection';
import { PrivacyTermsStatement } from '~/components/PrivacyTermsStatement';
import { Skeleton } from '~/components/ui/skeleton';

import messages from './PropertyContactSection.locale.json';

// Combined locale messages with deep merge
const combinedMessages = computed(() => {
	return deepMergeLocales(contactFormLocale, messages);
});

const strings = combinedMessages.value.pt;

const { translateByContext } = createLocaleTranslator(
	strings as Record<string, unknown>,
	'property'
);

const props = withDefaults(defineProps<PropertyContactSectionProps>(), {
	companyLogo: '',
	videoShorts: false
});

const emit = defineEmits<{
	(e: 'form-property-clicked', payload: OpenFormPropertyPayload): void;
	(e: 'open-contact-modal'): void;
}>();

// Use shared contact form state
const {
	name,
	phone,
	email,
	userMessage,
	errors,
	nameAttrs,
	phoneAttrs,
	emailAttrs,
	userMessageAttrs,
	meta,
	setFieldValue,
	isPhoneDisabled
} = useContactForm();

const isContactModalOpen = ref(false);

const openContactModal = () => {
	emit('open-contact-modal');
};

// Function to open modal directly at OTP step (used by mobile button)
function openContactModalAtOtp() {
	// Check if form is valid before proceeding
	if (!isTalkToBrokerButtonEnabled.value) return;

	// Use event-bus to open modal at OTP step
	// For logged users: jumps directly to confirmation
	// For non-logged users: goes to OTP step
	useEventBus<void>(EventBusKeys.openContactModalAtOtp).emit();
}

const agentFirstName = computed(() => getAgentFirstName(props.agent));
const agentName = computed(() => getAgentName(props.agent));
const firstName = computed(() => getAgentFirstName(props.agent));

const ctaText = computed(() => `${strings.speakWith} ${agentFirstName.value}`);

// Computed to determine if "Talk to broker" button should be enabled
const isTalkToBrokerButtonEnabled = computed(() => {
	// If form is valid, always enable
	if (meta.value.valid) return true;

	// If user is logged in and basic data is filled
	if (loggedIn.value && name.value && phone.value) {
		return true;
	}

	return false;
});

function handleOpenContactModal() {
	// Always open the modal at step 1 (contact-form) for desktop button
	// This ensures logged users see the form instead of jumping to confirmation
	emit('open-contact-modal');

	emit('form-property-clicked', {
		ctaText: ctaText.value,
		companyName: props.companyName,
		agentName: props.agent.name,
		askingPrice: props.askingPrice
	});
}

const getImageSrc = useImage();

const brokerSrc = computed(() =>
	getImageSrc(props.agent?.profilePicture.croppedUrl || '', {
		format: 'webp',
		width: 460
	})
);

// UI form state
const shouldExpandContainer = computed(() => {
	return !!(name.value?.trim() && phone.value?.trim());
});

const { loggedIn } = useUserSession();

// Function to submit form (opens normal modal)
function handleFormSubmit() {
	handleOpenContactModal();
}

// Function to handle expandable container toggle
function handleToggleExpanded(isExpanded: boolean) {
	// Log event if necessary
}

// Function to handle form data updates
function handleFormDataUpdate(
	updatedData: Partial<{
		name: string;
		phone: string;
		email: string;
		userMessage: string;
	}>
) {
	// Only update fields that are provided (not undefined) and are valid strings
	if ('name' in updatedData) {
		const value = updatedData.name?.trim() ?? '';
		setFieldValue('name', value);
	}
	if ('phone' in updatedData) {
		const value = updatedData.phone?.trim() ?? '';
		setFieldValue('phone', value);
	}
	if ('email' in updatedData) {
		const value = updatedData.email?.trim() ?? '';
		setFieldValue('email', value);
	}
	if ('userMessage' in updatedData) {
		const value = updatedData.userMessage?.trim() ?? '';
		setFieldValue('userMessage', value);
	}
}

// Configure initial form message
const initialUserMessage = computed<string>(() => {
	return translateByContext('form.userMessage.initialValue', {
		propertyCommercialId: props.commercialId,
		propertyRegion: props.region,
		propertyType: props.propertyType?.name?.toLowerCase() || ''
	});
});

// Set initial message when property loads
watch(
	initialUserMessage,
	message => {
		if (message && !userMessage.value) {
			setFieldValue('userMessage', message);
		}
	},
	{ immediate: true }
);
</script>

<template>
	<div
		v-if="isLoading"
		class="flex h-[240px] w-full items-center gap-4 bg-white-dark-2 p-5 md:p-8"
	>
		<Skeleton :is-active="true" variant="whiteHighlight" class="size-[180px]" />

		<div class="space-y-4">
			<Skeleton
				:is-active="true"
				variant="whiteHighlight"
				class="h-6 w-[120px]"
			/>
			<Skeleton
				:is-active="true"
				variant="whiteHighlight"
				class="h-[18px] w-[140px]"
			/>

			<div class="space-y-2">
				<Skeleton
					v-for="index in 3"
					:key="`property-contact-section-skeleton-line-${index}`"
					:is-active="true"
					variant="whiteHighlight"
					class="h-2 w-full last:w-3/5"
				/>
			</div>

			<Skeleton
				:is-active="true"
				variant="whiteHighlight"
				class="h-[50px] w-[200px]"
			/>
		</div>
	</div>

	<div v-else class="flex max-w-full flex-col gap-4">
		<h2
			class="font-matter-sq text-[28px] font-normal leading-[42px] text-primary"
		>
			{{ strings.speakWith }} {{ agentFirstName }}
		</h2>
		<div
			class="flex w-full flex-col justify-center gap-3 bg-secondary p-5 md:flex-row md:gap-5 md:px-8 md:py-7"
		>
			<div class="flex items-end">
				<BrokerAvatar
					:broker-src="brokerSrc"
					:company-src="companyLogo"
					:show-company-avatar="true"
					avatar-shape="square"
					company-avatar-shape="circle"
					class="h-[240px] w-full md:size-[152px]"
					sizes="containedRight"
					:broker-name="agentName"
					:company-name="companyName"
				/>
			</div>
			<div class="flex flex-col justify-between gap-3">
				<div class="flex flex-col gap-3 md:gap-2">
					<div class="flex flex-col gap-[0.12rem] md:gap-0">
						<h3
							class="font-matter-sq text-[20px] font-normal leading-[30px] tracking-[-0.025rem] text-primary"
						>
							{{ agentName }}
						</h3>
						<span class="font-sans text-sm font-normal text-primary-light-3">
							{{ strings.brokerAt }} {{ companyName }}
						</span>
					</div>
					<span
						class="hidden font-sans text-sm font-light italic text-primary md:block"
					>
						"{{
							strings.greeting.replace('{brokerName}', String(agentFirstName))
						}}"
					</span>
					<span class="block font-sans text-base text-primary md:hidden">
						Tem dúvidas ou quer agendar uma visita?
					</span>
				</div>

				<!-- Formulário integrado (apenas mobile) -->
				<div class="block w-full md:hidden">
					<ContactFormSection
						:form-data="{
							name,
							phone,
							email,
							userMessage
						}"
						:validation="{
							errors,
							isValid: meta.valid,
							isPhoneDisabled: !!isPhoneDisabled
						}"
						:form-attributes="{
							nameAttrs,
							phoneAttrs,
							emailAttrs,
							userMessageAttrs
						}"
						:broker-info="{
							broker: agent,
							name: agentName,
							avatarSrc: brokerSrc,
							message: translateByContext('brokerMessage'),
							atText: translateByContext('brokerAt', {
								brokerCompany: companyName
							}),
							companyName,
							companyLogo
						}"
						:text-content="{
							expandText: strings.showMoreInfo,
							collapseText: strings.showLessInfo,
							namePlaceholder: translateByContext('form.name.placeholder'),
							emailPlaceholder: translateByContext('form.email.placeholder'),
							userMessagePlaceholder: translateByContext(
								'form.userMessage.placeholder'
							)
						}"
						:app-state="{
							isPropertyContext: false,
							isUserLoggedIn: loggedIn,
							shouldExpandContainer
						}"
						:analytics-data="{
							propertyCommercialId: props.commercialId,
							askingPrice: props.askingPrice,
							videoShorts: props.videoShorts
						}"
						class="!px-0 !pt-3 md:!pt-4"
						@update:form-data="handleFormDataUpdate"
						@submit="handleFormSubmit"
						@toggle-expanded="handleToggleExpanded"
					/>
					<Button
						size="md"
						data-test-id="details-contact-button"
						class="mt-6 flex w-full items-center gap-3 text-base font-medium"
						@click="openContactModalAtOtp"
					>
						<IconMaterialSymbolsLightChatBubbleOutlineSharp
							class="size-6 text-beige"
						/>
						Entrar em contato
					</Button>
					<PrivacyTermsStatement :messages="combinedMessages" />
				</div>

				<!-- Botão original (apenas desktop) -->
				<div class="hidden w-full items-center justify-start md:flex md:h-full">
					<Button
						data-test-id="details-contact-button-property"
						variant="primary-dark"
						class="h-full w-fit justify-start border border-white-dark-3 px-4 py-3"
						@click="handleOpenContactModal"
					>
						<IconMaterialSymbolsLightChatBubbleOutline class="size-5" />
						<span class="font-sans text-base font-normal text-primary">
							{{ ctaText }}
						</span>
					</Button>
				</div>
			</div>
		</div>
	</div>
</template>
